import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { KibCloseFullscreenIcon } from '@chewy/kib-icons-react'
import classNames from 'classnames'
import { type InlineSearchHandle, Text } from '@pbt/pbt-ui-components'

import RhapTextButton from '~/components/common/RhapTextButton'
import type { KibSearchHandle } from '~/components/common/search/KibSearchWrapper'
import { ChargeQueryParams } from '~/types'
import type { BaseChargeTabProps } from '~/types/entities/charges'

import { useLeftMenuWidth } from './hooks/useLeftMenuWidth'
import SearchChargeTab from './tabs/search/SearchChargeTab'

import styles from './ChargesWidgetExpanded.module.scss'

// Constants
const HEADER_HEIGHT = 72
const MOBILE_BREAKPOINT = 960

export interface ChargesWidgetExpandedProps extends BaseChargeTabProps {
  onCollapse: () => void
  patientName: string | undefined
  renderChargesTable: (expanded?: boolean) => React.ReactNode
}

const ChargesWidgetExpanded = ({
  disabled,
  handleEditListItem,
  handleRefill,
  handleSavePrescription,
  logItemStateGetter,
  openPrescriptionDialog,
  onCollapse,
  patientName,
  renderChargesTable,
}: ChargesWidgetExpandedProps) => {
  const { t } = useTranslation(['Common', 'Payments'])
  const [searchParams, setSearchParams] = useSearchParams()
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < MOBILE_BREAKPOINT,
  )

  const leftMenuWidth = useLeftMenuWidth()
  const searchRef = useRef<InlineSearchHandle | KibSearchHandle>(null)
  const searchTerm =
    searchParams.get(ChargeQueryParams.CHARGE_SEARCH_QUERY) ?? ''

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const clearSearch = () => {
    if (searchRef.current?.clearSearch) {
      searchRef.current.clearSearch()
      searchParams.delete(ChargeQueryParams.CHARGE_SEARCH_QUERY)
      setSearchParams(searchParams)
    }
  }

  // Calculate position based on header height and left menu width
  const containerStyle: React.CSSProperties = {
    top: isMobile ? 0 : HEADER_HEIGHT,
    left: leftMenuWidth,
    right: 0,
  }

  return (
    <div className={styles.container} style={containerStyle}>
      <div className={styles.header}>
        <Text strong variant="h4">
          {t('Payments:CHARGES_WITH_PATIENT_NAME', {
            patientName: patientName || t('Common:PET'),
          })}
        </Text>
        <RhapTextButton
          emphasis="secondary"
          icon={<KibCloseFullscreenIcon />}
          iconPosition="end"
          size="small"
          theme="action"
          onClick={onCollapse}
        >
          {t('Common:COLLAPSE_ACTION')}
        </RhapTextButton>
      </div>
      <div className={styles.content}>
        <div
          className={classNames(styles.chargesItemsWrapper, {
            [styles.chargesItemsWrapperExpanded]: searchTerm,
          })}
        >
          <SearchChargeTab
            clearSearch={clearSearch}
            disabled={disabled}
            handleEditListItem={handleEditListItem}
            handleRefill={handleRefill}
            handleSavePrescription={handleSavePrescription}
            logItemStateGetter={logItemStateGetter}
            openPrescriptionDialog={openPrescriptionDialog}
            searchRef={searchRef}
          />
        </div>
        {!searchTerm && (
          <div className={styles.tableWrapper}>{renderChargesTable(true)}</div>
        )}
      </div>
    </div>
  )
}

export default ChargesWidgetExpanded
